import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoginPromptDialog from '../components/LoginPromptDialog';
import { recordGuestGatedOpen } from '../components/GuestUpgradeNudge';
import { savePendingAction } from '../utils/pendingAction';

/**
 * useAuthGate — gate premium/interactive actions behind login for guests.
 *
 * Usage:
 *   const { user, gate, dialog } = useAuthGate();
 *   <button onClick={() => gate(() => doThing(), "Strong's numbers")}>…</button>
 *   {dialog}   // render once in the component tree
 *
 * `gate(fn, featureLabel)` runs `fn` when logged in, otherwise opens the
 * friendly sign-in prompt. `requireAuth(featureLabel)` returns true when
 * allowed (already signed in) and opens the prompt + returns false otherwise.
 */
export const useAuthGate = () => {
  const { user } = useAuth();
  const [feature, setFeature] = useState(null);

  // Optional `pendingAction` ({type, target, text}) is replayed automatically after sign-in.
  const requireAuth = useCallback((featureLabel, pendingAction) => {
    if (user) return true;
    savePendingAction(pendingAction);
    setFeature(featureLabel || null);
    recordGuestGatedOpen();
    return false;
  }, [user]);

  const gate = useCallback((fn, featureLabel) => {
    if (user) return fn();
    setFeature(featureLabel || null);
    recordGuestGatedOpen();
    return undefined;
  }, [user]);

  const dialog = (
    <LoginPromptDialog open={feature !== null} feature={feature} onClose={() => setFeature(null)} />
  );

  return { user, isGuest: !user, gate, requireAuth, dialog };
};

export default useAuthGate;
